import { useEffect, useMemo, useState } from "react";
import { Button, Card, DatePicker, message, Space, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import PvTable from "../../components/Table/PvTable";
import TermSelect from "../../components/TermSelect";
import ConfirmDelete from "../../components/ConfirmDelete/ConfirmDelete";
import useFsrAdminService from "../../services/FsrAdmin/fsrAdminService";

const TermDateSection = () => {
  const {
    getTermDates,
    listLoading,
    listResponse,
    createTermDates,
    updateTermDates,
    saveLoading,
    deleteTermDates,
  } = useFsrAdminService();

  const [termLabel, setTermLabel] = useState();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    getTermDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableData = useMemo(
    () =>
      (listResponse?.data ?? listResponse ?? []).map((item, index) => ({
        key: index + 1,
        ...item,
      })),
    [listResponse]
  );

  const handleEdit = (record) => {
    setEditingId(record.id);
    setTermLabel(record.term_label);
    setStartDate(dayjs(record.start_date));
    setEndDate(dayjs(record.end_date));
  };

  const handleSave = async () => {
    if (!termLabel || !startDate || !endDate) {
      message.error("Please select a term and both dates.");
      return;
    }

    if (endDate.isBefore(startDate)) {
      message.error("End date cannot be before start date.");
      return;
    }

    // A term already has dates set — only the row's own Edit flow may
    // change them; block accidental overwrites from the Add form. Delete
    // the existing entry first to set it again from scratch. (The backend
    // enforces this too — this is just for immediate feedback.)
    const existing = tableData.find((t) => t.term_label === termLabel);
    if (existing && existing.id !== editingId) {
      message.error(
        "This term already has a Start and End Date set. Delete it first if you want to set new dates."
      );
      return;
    }

    try {
      if (editingId) {
        await updateTermDates(
          editingId,
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      } else {
        await createTermDates(
          termLabel,
          startDate.format("YYYY-MM-DD"),
          endDate.format("YYYY-MM-DD")
        );
      }
      message.success("Term dates saved.");
      setEditingId(null);
      setTermLabel(undefined);
      setStartDate(null);
      setEndDate(null);
      getTermDates();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to save term dates.";
      message.error(msg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTermDates(id);
      message.success("Term dates deleted.");
      getTermDates();
    } catch {
      message.error("Failed to delete term dates.");
    }
  };

  const columns = [
    { title: "No.", dataIndex: "key", width: 60 },
    { title: "Term", dataIndex: "term_label", key: "term_label" },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <ConfirmDelete
            isService
            isIcon
            buttonText="Delete"
            onDeleted={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <Card title="Term Date Creation">
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Space wrap align="start">
          <TermSelect
            extended
            width={320}
            value={termLabel}
            onChange={setTermLabel}
          />
          <DatePicker
            placeholder="Start Date"
            value={startDate}
            onChange={setStartDate}
          />
          <DatePicker
            placeholder="End Date"
            value={endDate}
            onChange={setEndDate}
          />
          <Button
            type="primary"
            style={{ background: "#00573F" }}
            loading={saveLoading}
            onClick={handleSave}
          >
            Save
          </Button>
        </Space>

        <PvTable
          data={tableData}
          columns={columns}
          loading={listLoading}
          onRowClick={() => {}}
        />
      </Space>
    </Card>
  );
};

export default TermDateSection;
